import React from "react";
import BasicDataCollectionForm from "./basic-data-collection-form";
import AgematchForm from "./agematch-form";
import PhoneMatchForm from "./phonematch-form";

// A field with `type: "address"` renders as a full address block
// (street + city + state + zip) unless the config opts out via
// `showCityState: false`, in which case it renders as just street
// + zip. Only the full-address form needs its own multi-step
// screen; the compact street+zip variant fits inline with the
// rest of a flat form.
const isFullAddress = (f) => f?.type === "address" && f?.showCityState !== false;
const isShortAddress = (f) => f?.type === "address" && f?.showCityState === false;

// "Visual" input count for a given field config. Full addresses
// count as 4 (street/city/state/zip); short addresses count as 2
// (street/zip); every other field type is one input.
const countInputs = (fields = []) =>
  fields.reduce((sum, f) => {
    if (isFullAddress(f)) return sum + 4;
    if (isShortAddress(f)) return sum + 2;
    return sum + 1;
  }, 0);

// Forms above this input count, or any form with a FULL address
// field, render through the multi-step AgematchForm instead of the
// flat BasicDataCollectionForm. Full-address forces multi-step
// because the address modal needs its own screen — a short
// address (street+zip only) fits inline and doesn't.
const SIMPLE_FORM_INPUT_LIMIT = 5;

const DataCollectionForm = ({
  title,
  subtitle,
  icon,
  componentKey,
  endpoint,
  config,
  buttonText,
  loadingText,
  reAttempts = 0,
  onApiResponse,
  // Prefilled-form behaviour, passed through to BasicDataCollectionForm.
  // skipInitialAutoSubmit: show the form even when every field is already
  // valid from session data. autoSubmit: submit once on mount.
  skipInitialAutoSubmit = false,
  autoSubmit = false,
}) => {
  // PhoneMatchForm owns the OTP flow that BasicDataCollectionForm
  // can't replicate.
  if (componentKey === "phonematch") {
    return (
      <PhoneMatchForm
        title={title}
        subtitle={subtitle}
        icon={icon}
        componentKey={componentKey}
        endpoint={endpoint}
        config={config}
        buttonText={buttonText}
        reAttempts={reAttempts}
        onApiResponse={onApiResponse}
      />
    );
  }

  // Long forms (>5 visual inputs OR any full-address field) get
  // chunked into the multi-step AgematchForm — Name → Address →
  // DOB / other. A short-address field (showCityState: false)
  // renders inline as street+zip and stays in the flat form.
  const hasFullAddress = (config?.fields || []).some(isFullAddress);
  const useMultiStep =
    hasFullAddress || countInputs(config?.fields) > SIMPLE_FORM_INPUT_LIMIT;

  if (useMultiStep) {
    return (
      <AgematchForm
        title={title}
        subtitle={subtitle}
        icon={icon}
        componentKey={componentKey}
        endpoint={endpoint}
        config={config}
        buttonText={buttonText}
        reAttempts={reAttempts}
        onApiResponse={onApiResponse}
        autoSubmitPrefilled={autoSubmit || !skipInitialAutoSubmit}
      />
    );
  }

  // Short forms stay flat as a single page of floating-label inputs.
  return (
    <BasicDataCollectionForm
      title={title}
      subtitle={subtitle}
      icon={icon}
      componentKey={componentKey}
      endpoint={endpoint}
      config={config}
      buttonText={buttonText}
      loadingText={loadingText}
      reAttempts={reAttempts}
      onApiResponse={onApiResponse}
      skipInitialAutoSubmit={skipInitialAutoSubmit}
      autoSubmit={autoSubmit}
    />
  );
};

export default DataCollectionForm;
